/**
 * useDebounce Hook
 * Custom hook for debouncing values to optimize performance in search and input scenarios
 */

import { useState, useEffect, useCallback } from 'react';

// ============================================================================
// Types
// ============================================================================

interface UseDebounceOptions {
  /** Delay in milliseconds before the value updates */
  delay?: number;
  /** Whether to update immediately on first change */
  immediate?: boolean;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Custom hook that returns a debounced value
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds (default: 300ms)
 * @param options - Additional options
 * @returns The debounced value
 */
export function useDebounce<T>(
  value: T,
  delay = 300,
  options: UseDebounceOptions = {},
): T {
  const { immediate = false } = options;
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const safeDelay = delay >= 0 ? delay : 0;

  useEffect(() => {
    // If immediate is true and this is the first render, set value immediately
    if (immediate && debouncedValue === value) {
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, safeDelay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, safeDelay, immediate, debouncedValue]);

  return debouncedValue;
}

/**
 * Hook that returns a debounced callback function
 * @param callback - The callback function to debounce
 * @param delay - Delay in milliseconds (default: 300ms)
 * @param deps - Dependencies array for useCallback
 * @returns The debounced callback function
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay = 300,
  deps: React.DependencyList = [],
): T & { cancel: () => void; flush: () => void } {
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const cancel = useCallback(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
  }, [timeoutId]);

  const flush = useCallback(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
  }, [timeoutId]);

  const debouncedCallback = useCallback(
    ((...args: Parameters<T>) => {
      cancel();

      const newTimeoutId = setTimeout(() => {
        callback(...args);
      }, delay);

      setTimeoutId(newTimeoutId);
    }) as T,
    [callback, delay, cancel, ...deps],
  );

  // Add cancel and flush methods to the callback
  Object.assign(debouncedCallback, { cancel, flush });

  return debouncedCallback as T & { cancel: () => void; flush: () => void };
}

/**
 * Hook for debouncing search queries
 * @param query - The search query
 * @param delay - Delay in milliseconds (default: 300ms)
 * @returns Object with debounced query and loading state
 */
export function useDebouncedSearch(
  query: string,
  delay = 300,
): {
  debouncedQuery: string;
  isSearching: boolean;
  hasStartedTyping: boolean;
} {
  const [isSearching, setIsSearching] = useState(false);
  const [hasStartedTyping, setHasStartedTyping] = useState(false);

  const debouncedQuery = useDebounce(query, delay);

  useEffect(() => {
    if (query && !hasStartedTyping) {
      setHasStartedTyping(true);
    }
  }, [query, hasStartedTyping]);

  useEffect(() => {
    if (hasStartedTyping) {
      if (query !== debouncedQuery) {
        setIsSearching(true);
      } else {
        setIsSearching(false);
      }
    }
  }, [query, debouncedQuery, hasStartedTyping]);

  return {
    debouncedQuery,
    isSearching,
    hasStartedTyping,
  };
}

/**
 * Hook for debouncing form input values
 * @param initialValue - Initial value
 * @param onChange - Callback when debounced value changes
 * @param delay - Delay in milliseconds (default: 300ms)
 * @returns Object with current value, debounced value, and setter
 */
export function useDebouncedInput(
  initialValue = '',
  onChange?: (value: string) => void,
  delay = 300,
): {
  value: string;
  debouncedValue: string;
  setValue: (value: string) => void;
  isPending: boolean;
} {
  const [value, setValue] = useState(initialValue);
  const debouncedValue = useDebounce(value, delay);
  const isPending = value !== debouncedValue;

  useEffect(() => {
    if (onChange && debouncedValue !== initialValue) {
      onChange(debouncedValue);
    }
  }, [debouncedValue, onChange, initialValue]);

  return {
    value,
    debouncedValue,
    setValue,
    isPending,
  };
}

/**
 * Hook for debouncing numeric input values
 * @param initialValue - Initial value
 * @param onChange - Callback when debounced value changes
 * @param delay - Delay in milliseconds (default: 300ms)
 * @returns Object with current value, debounced value, and setter
 */
export function useDebouncedNumber(
  initialValue = 0,
  onChange?: (value: number) => void,
  delay = 300,
): {
  value: number;
  debouncedValue: number;
  setValue: (value: number) => void;
  isPending: boolean;
} {
  const [value, setValue] = useState(initialValue);
  const debouncedValue = useDebounce(value, delay);
  const isPending = value !== debouncedValue;

  useEffect(() => {
    if (onChange && debouncedValue !== initialValue) {
      onChange(debouncedValue);
    }
  }, [debouncedValue, onChange, initialValue]);

  return {
    value,
    debouncedValue,
    setValue,
    isPending,
  };
}

export default useDebounce;
